import React, { useState, useCallback } from "react"
import { Eye, EyeOff, Check, X } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"

export function PasswordInput({
    label = "Password",
    className,
    value,
    ...props
}) {
    const [showPassword, setShowPassword] = useState(false)

    // Define each validation requirement and its checker
    const validations = [
        {
            label: "Minimum 8 characters",
            test: (val) => val.length >= 8,
        },
        {
            label: "At least one uppercase",
            test: (val) => /[A-Z]/.test(val),
        },
        {
            label: "At least one special character",
            test: (val) => /[^A-Za-z0-9]/.test(val),
        },
    ]

    // Toggle password visibility
    const handleTogglePassword = useCallback(() => {
        setShowPassword((prev) => !prev)
    }, [])

    return (
        <div className={`space-y-2 ${className ?? ""}`}>
            <Label htmlFor="password">{label}</Label>
            {/* Checklist of requirements */}
            <div className="space-y-1">
                {validations.map(({ label, test }) => {
                    const isValid = test(value)
                    return (
                        <div key={label} className="flex items-center text-sm">
                            {isValid ? (
                                <Check className="mr-2 h-4 w-4 text-green-500" />
                            ) : (
                                <X className="mr-2 h-4 w-4 text-red-500" />
                            )}
                            <span>{label}</span>
                        </div>
                    )
                })}
            </div>
            <div className="relative">
                <Input
                    id="password"
                    // We'll flip between "text" and "password" for show/hide
                    type={showPassword ? "text" : "password"}
                    value={value}
                    {...props}
                /**
                 * By doing "...props" here, we ensure any required, pattern, title, etc.
                 * are forwarded from the parent component to this actual <input>.
                 */
                />
                <button
                    type="button"
                    onClick={handleTogglePassword}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
            </div>
        </div>
    )
}
